import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead, { SEO_CONFIG } from '../components/SEOHead';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, X, Building, MapPin, Home, Euro, Maximize, BedDouble, Bath, Car, TreePine } from 'lucide-react';

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    area: searchParams.get('area') || '',
    property_type: searchParams.get('property_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    min_sqm: searchParams.get('min_sqm') || '',
    max_sqm: searchParams.get('max_sqm') || '',
    min_rooms: searchParams.get('min_rooms') || '',
    max_rooms: searchParams.get('max_rooms') || '',
    bathrooms: searchParams.get('bathrooms') || '',
    floor: searchParams.get('floor') || '',
    parking: searchParams.get('parking') || '',
    garden: searchParams.get('garden') || '',
    balcony: searchParams.get('balcony') || '',
    elevator: searchParams.get('elevator') || '',
    auction_status: searchParams.get('auction_status') || ''
  });

  const propertyTypes = [
    { value: 'appartamento', label: 'Appartamento' },
    { value: 'villa', label: 'Villa' },
    { value: 'casa', label: 'Casa Indipendente' },
    { value: 'attico', label: 'Attico' },
    { value: 'mansarda', label: 'Mansarda' },
    { value: 'loft', label: 'Loft' },
    { value: 'bilocale', label: 'Bilocale' },
    { value: 'trilocale', label: 'Trilocale' },
    { value: 'quadrilocale', label: 'Quadrilocale' },
    { value: 'rustico', label: 'Rustico / Casale' },
    { value: 'palazzo', label: 'Palazzo / Stabile' },
    { value: 'ufficio', label: 'Ufficio' },
    { value: 'negozio', label: 'Negozio' },
    { value: 'box', label: 'Box / Garage' },
    { value: 'terreno', label: 'Terreno' }
  ];

  const priceRanges = [
    { value: '50000', label: '€ 50.000' },
    { value: '100000', label: '€ 100.000' },
    { value: '150000', label: '€ 150.000' },
    { value: '200000', label: '€ 200.000' },
    { value: '250000', label: '€ 250.000' },
    { value: '300000', label: '€ 300.000' },
    { value: '400000', label: '€ 400.000' },
    { value: '500000', label: '€ 500.000' },
    { value: '750000', label: '€ 750.000' },
    { value: '1000000', label: '€ 1.000.000' },
    { value: '1500000', label: '€ 1.500.000' },
    { value: '2000000', label: '€ 2.000.000+' }
  ];

  const surfaceRanges = [
    { value: '30', label: '30 m²' },
    { value: '50', label: '50 m²' },
    { value: '70', label: '70 m²' },
    { value: '90', label: '90 m²' },
    { value: '120', label: '120 m²' },
    { value: '150', label: '150 m²' },
    { value: '200', label: '200 m²' },
    { value: '300', label: '300 m²' },
    { value: '500', label: '500 m²+' }
  ];

  const roomOptions = [
    { value: '1', label: '1 locale' },
    { value: '2', label: '2 locali' },
    { value: '3', label: '3 locali' },
    { value: '4', label: '4 locali' },
    { value: '5', label: '5+ locali' }
  ];

  const bathroomOptions = [
    { value: '1', label: '1 bagno' },
    { value: '2', label: '2 bagni' },
    { value: '3', label: '3+ bagni' }
  ];

  const floorOptions = [
    { value: 'terra', label: 'Piano terra' },
    { value: 'intermedio', label: 'Piano intermedio' },
    { value: 'alto', label: 'Piano alto' },
    { value: 'ultimo', label: 'Ultimo piano' }
  ];

  const auctionStatuses = [
    { value: 'published', label: 'In Vendita' },
    { value: 'scheduled', label: 'Gara Programmata' },
    { value: 'active', label: 'Gara in Corso' }
  ];

  useEffect(() => {
    fetchProperties();
  }, [currentPage]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', 12);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(`/properties?${params.toString()}`);
      setProperties(response.data.properties);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    setSearchParams(params);
    
    fetchProperties();
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      area: '',
      property_type: '',
      min_price: '',
      max_price: '',
      min_sqm: '',
      max_sqm: '',
      min_rooms: '',
      max_rooms: '',
      bathrooms: '',
      floor: '',
      parking: '',
      garden: '',
      balcony: '',
      elevator: '',
      auction_status: ''
    });
    setSearchParams({});
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...SEO_CONFIG.properties} />
      <Header />

      {/* Page Header */}
      <section className="section-neutral py-16 lg:py-24">
        <div className="container-apple">
          <span className="text-[14px] font-medium text-[#0071e3] uppercase tracking-wider">
            Acquista casa al miglior prezzo
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1D1D1F] mt-4 mb-4">
            Immobili in Vendita
          </h1>
          <p className="text-[17px] text-[#86868B] max-w-xl">
            Trova casa al miglior prezzo. Immobili selezionati in tutta Italia con trasparenza e risparmio garantito.
          </p>
        </div>
      </section>

      <div className="container-apple py-12">
        {/* Search & Filters Bar - Stile Immobiliare.it */}
        <div className="premium-card p-6 mb-10">
          {/* Prima riga - Filtri principali */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Località */}
            <div className="lg:col-span-1">
              <label className="text-xs font-medium text-[#86868B] uppercase tracking-wider mb-1.5 block">
                Dove
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#86868B]" strokeWidth={1.5} />
                <input
                  placeholder="Città o zona..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="input-apple pl-10 h-12"
                  data-testid="search-city-input"
                />
              </div>
            </div>

            {/* Tipologia */}
            <div>
              <label className="text-xs font-medium text-[#86868B] uppercase tracking-wider mb-1.5 block">
                Tipologia
              </label>
              <Select 
                value={filters.property_type} 
                onValueChange={(value) => setFilters({ ...filters, property_type: value })}
              >
                <SelectTrigger data-testid="filter-property-type" className="input-apple h-12">
                  <Home className="h-4 w-4 text-[#86868B] mr-2" />
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl max-h-80">
                  {propertyTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prezzo */}
            <div>
              <label className="text-xs font-medium text-[#86868B] uppercase tracking-wider mb-1.5 block">
                Prezzo
              </label>
              <div className="flex gap-2">
                <Select 
                  value={filters.min_price} 
                  onValueChange={(value) => setFilters({ ...filters, min_price: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="Da" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {priceRanges.map((price) => (
                      <SelectItem key={price.value} value={price.value}>
                        {price.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.max_price} 
                  onValueChange={(value) => setFilters({ ...filters, max_price: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="A" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {priceRanges.map((price) => (
                      <SelectItem key={price.value} value={price.value}>
                        {price.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Superficie */}
            <div>
              <label className="text-xs font-medium text-[#86868B] uppercase tracking-wider mb-1.5 block">
                Superficie
              </label>
              <div className="flex gap-2">
                <Select 
                  value={filters.min_sqm} 
                  onValueChange={(value) => setFilters({ ...filters, min_sqm: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="Da" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {surfaceRanges.map((sqm) => (
                      <SelectItem key={sqm.value} value={sqm.value}>
                        {sqm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.max_sqm} 
                  onValueChange={(value) => setFilters({ ...filters, max_sqm: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="A" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {surfaceRanges.map((sqm) => (
                      <SelectItem key={sqm.value} value={sqm.value}>
                        {sqm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Locali */}
            <div>
              <label className="text-xs font-medium text-[#86868B] uppercase tracking-wider mb-1.5 block">
                Locali
              </label>
              <div className="flex gap-2">
                <Select 
                  value={filters.min_rooms} 
                  onValueChange={(value) => setFilters({ ...filters, min_rooms: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="Da" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {roomOptions.map((room) => (
                      <SelectItem key={room.value} value={room.value}>
                        {room.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.max_rooms} 
                  onValueChange={(value) => setFilters({ ...filters, max_rooms: value })}
                >
                  <SelectTrigger className="input-apple h-12 flex-1">
                    <SelectValue placeholder="A" />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                    {roomOptions.map((room) => (
                      <SelectItem key={room.value} value={room.value}>
                        {room.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Seconda riga - Azioni e filtri avanzati */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-6 pt-6 border-t border-black/5">
            <div className="flex flex-wrap gap-3">
              {/* More Filters Toggle */}
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  showFilters 
                    ? 'bg-[#0071e3] text-white' 
                    : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
                }`}
                data-testid="toggle-filters-btn"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Altri filtri
              </button>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button 
                  onClick={clearFilters} 
                  className="inline-flex items-center px-4 py-2.5 rounded-full text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Cancella filtri
                </button>
              )}
            </div>

            {/* Search Button */}
            <button 
              onClick={handleSearch} 
              className="btn-apple-primary w-full sm:w-auto"
              data-testid="search-btn"
            >
              <Search className="h-4 w-4 mr-2" />
              Cerca immobili
            </button>
          </div>

          {/* Filtri Avanzati - Espandibili */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-black/5">
              <h3 className="text-sm font-semibold text-[#1D1D1F] mb-4">Filtri avanzati</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {/* Quartiere/Zona */}
                <div>
                  <label className="text-xs font-medium text-[#86868B] mb-1.5 block">Zona</label>
                  <input
                    placeholder="Quartiere..."
                    value={filters.area}
                    onChange={(e) => setFilters({ ...filters, area: e.target.value })}
                    className="input-apple h-11 text-sm"
                    data-testid="filter-area-input"
                  />
                </div>

                {/* Bagni */}
                <div>
                  <label className="text-xs font-medium text-[#86868B] mb-1.5 block">Bagni</label>
                  <Select 
                    value={filters.bathrooms} 
                    onValueChange={(value) => setFilters({ ...filters, bathrooms: value })}
                  >
                    <SelectTrigger className="input-apple h-11">
                      <Bath className="h-3.5 w-3.5 text-[#86868B] mr-1.5" />
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                      {bathroomOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Piano */}
                <div>
                  <label className="text-xs font-medium text-[#86868B] mb-1.5 block">Piano</label>
                  <Select 
                    value={filters.floor} 
                    onValueChange={(value) => setFilters({ ...filters, floor: value })}
                  >
                    <SelectTrigger className="input-apple h-11">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                      {floorOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stato Vendita */}
                <div>
                  <label className="text-xs font-medium text-[#86868B] mb-1.5 block">Stato</label>
                  <Select 
                    value={filters.auction_status} 
                    onValueChange={(value) => setFilters({ ...filters, auction_status: value })}
                  >
                    <SelectTrigger className="input-apple h-11">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 backdrop-blur-xl border-black/5 rounded-xl">
                      {auctionStatuses.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Caratteristiche */}
                <div className="col-span-2">
                  <label className="text-xs font-medium text-[#86868B] mb-1.5 block">Caratteristiche</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilters({ ...filters, parking: filters.parking ? '' : 'si' })}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filters.parking 
                          ? 'bg-[#0071e3] text-white' 
                          : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
                      }`}
                    >
                      <Car className="h-3 w-3 mr-1" />
                      Parcheggio
                    </button>
                    <button
                      onClick={() => setFilters({ ...filters, garden: filters.garden ? '' : 'si' })}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filters.garden 
                          ? 'bg-[#0071e3] text-white' 
                          : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
                      }`}
                    >
                      <TreePine className="h-3 w-3 mr-1" />
                      Giardino
                    </button>
                    <button
                      onClick={() => setFilters({ ...filters, balcony: filters.balcony ? '' : 'si' })}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filters.balcony 
                          ? 'bg-[#0071e3] text-white' 
                          : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
                      }`}
                    >
                      Balcone
                    </button>
                    <button
                      onClick={() => setFilters({ ...filters, elevator: filters.elevator ? '' : 'si' })}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        filters.elevator 
                          ? 'bg-[#0071e3] text-white' 
                          : 'bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E8E8ED]'
                      }`}
                    >
                      Ascensore
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-[#F5F5F7] rounded-3xl h-80 animate-pulse" />
            ))}
          </div>
        ) : properties.length > 0 ? (
          <>
            <div className="flex justify-between items-center mb-8">
              <p className="text-[15px] text-[#86868B]">
                <span className="font-semibold text-[#1D1D1F]">{properties.length}</span> immobili trovati
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="properties-grid">
              {properties.map((property) => (
                <PropertyCard key={property.property_id} property={property} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12 space-x-3">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-apple-outline disabled:opacity-50"
                >
                  Precedente
                </button>
                <div className="flex items-center px-6 text-[15px] text-[#86868B]">
                  Pagina {currentPage} di {totalPages}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-apple-outline disabled:opacity-50"
                >
                  Successiva
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-24 premium-card">
            <div className="w-16 h-16 bg-[#F5F5F7] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building className="h-8 w-8 text-[#86868B]" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">Nessun immobile trovato</h3>
            <p className="text-[15px] text-[#86868B] mb-6">Prova a modificare i filtri di ricerca</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-apple-outline">
                Cancella tutti i filtri
              </button>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
